export const DATAWRAPPER_URL = /^https:\/\/datawrapper\.dwcdn\.net\/([A-Za-z0-9]+)\/(\d+)\/$/
export const TYPEFORM_LIVE_ID = /^[0-9A-Z]{26}$/
export const TYPEFORM_FORM_URL =
  /^https:\/\/(?:form\.typeform\.com|[a-z0-9-]+\.typeform\.com)\/to\/[A-Za-z0-9]+$/

const TWEET_URL =
  /^https?:\/\/(?:www\.|mobile\.)?(?:twitter|x)\.com\/(\w{1,15})\/status\/(\d+)\/?(?:[?#].*)?$/i
const INSTAGRAM_URL = /^https?:\/\/(?:www\.)?instagram\.com\/(p|reel|tv)\/([\w-]+)\/?(?:[?#].*)?$/i
const TIKTOK_URL = /^https?:\/\/(?:www\.)?tiktok\.com\/(@[\w.-]+)\/video\/(\d+)\/?(?:[?#].*)?$/i
export const EXTERNAL_VIDEO_URL = /^https:\/\/[^\s?#]+\.(?:mp4|webm|mov)(?:[?#]\S*)?$/i

export type SocialPlatform = 'twitter' | 'instagram' | 'tiktok'

export interface SocialEmbedTarget {
  platform: SocialPlatform
  postId: string
  canonicalUrl: string
}

export function parseSocialUrl(url: string): SocialEmbedTarget | null {
  const trimmed = url.trim()
  const tweet = TWEET_URL.exec(trimmed)
  if (tweet) {
    return {
      platform: 'twitter',
      postId: tweet[2],
      canonicalUrl: `https://twitter.com/${tweet[1]}/status/${tweet[2]}`,
    }
  }
  const instagram = INSTAGRAM_URL.exec(trimmed)
  if (instagram) {
    return {
      platform: 'instagram',
      postId: instagram[2],
      canonicalUrl: `https://www.instagram.com/${instagram[1].toLowerCase()}/${instagram[2]}/`,
    }
  }
  const tiktok = TIKTOK_URL.exec(trimmed)
  if (tiktok) {
    return {
      platform: 'tiktok',
      postId: tiktok[2],
      canonicalUrl: `https://www.tiktok.com/${tiktok[1]}/video/${tiktok[2]}`,
    }
  }
  return null
}

export function parseDatawrapperUrl(url: string): { chartId: string; version: string } | null {
  const match = DATAWRAPPER_URL.exec(url.trim())
  return match ? { chartId: match[1], version: match[2] } : null
}

const YOUTUBE_VIDEO_ID = /^[\w-]{11}$/
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
])

export interface YoutubeEmbedTarget {
  videoId: string
  /** Start offset in seconds, from `t=` / `start=` */
  startSeconds?: number
}

function parseStartSeconds(params: URLSearchParams): number | undefined {
  const raw = params.get('start') ?? params.get('t')
  if (!raw) return undefined
  if (/^\d+$/.test(raw)) return Number(raw)
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(raw)
  if (!match) return undefined
  const seconds = Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0)
  return seconds > 0 ? seconds : undefined
}

/** Extracts the video id from watch, embed, shorts, live, youtu.be and nocookie URLs. */
export function parseYoutubeUrl(url: string): YoutubeEmbedTarget | null {
  let parsed: URL
  try {
    parsed = new URL(url.trim())
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null

  const host = parsed.hostname.toLowerCase()
  const segments = parsed.pathname.split('/').filter(Boolean)
  let videoId: string | undefined

  if (host === 'youtu.be') {
    videoId = segments[0]
  } else if (YOUTUBE_HOSTS.has(host)) {
    if (segments[0] === 'watch') videoId = parsed.searchParams.get('v') ?? undefined
    else if (['embed', 'shorts', 'live'].includes(segments[0])) videoId = segments[1]
  }

  if (!videoId || !YOUTUBE_VIDEO_ID.test(videoId)) return null
  return { videoId, startSeconds: parseStartSeconds(parsed.searchParams) }
}
